import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { RequireAuth } from "utils/RequireAuth";
import { useUser } from "stores/useUser";
import RequireRole from "utils/RequireRole";
import useExtensioniFrameListener from "hooks/useExtensioniFrameListener";
import Events from "pages/Events/Events";
import ActionsInsights from "pages/Insights/pages/ActionsInsights/ActionsInsights";
import MyOrg from "pages/Insights/pages/Organizatoin/MyOrg";
import GeneralInfo from "pages/Organization/pages/GeneralInfo/GeneralInfo";
import AdminLayout from "pages/Organization/AdminLayout";
import ManageGoals from "pages/Organization/pages/ManageGoals/ManageGoals";
import SettingsPage from "pages/Organization/pages/Settings/Settings";
import ManageUsers from "pages/Organization/pages/ManageUsers/ManageUsers";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import InsightsLayout from "pages/Insights/InsightsLayout";
// Eager on purpose: a Suspense fallback must never be able to suspend itself.
import Fallback from "components/FallBack/Fallback";

const Analytics = lazy(() =>
  import("pages/Insights/pages/Analytics/Analytics")
);
const DirectReport = lazy(() =>
  import("pages/Insights/pages/DirectReportees/DirectReport/DirectReport")
);
const DirectReportees = lazy(() =>
  import("pages/Insights/pages/DirectReportees/DirectReportees")
);
const AsAttendee = lazy(() =>
  import("pages/Insights/pages/MeetingTime/AsAttendee/AsAttendee")
);
const AsOrganizer = lazy(() =>
  import("pages/Insights/pages/MeetingTime/AsOrganizer/AsOrganizer")
);
const OptimizeWeek = lazy(() =>
  import("pages/Insights/pages/MeetingTime/OptimizeWeek/OptimizeWeek")
);
const PrivacyPolicy = lazy(() => import("pages/PrivacyPolicy/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("pages/TermsOfUse/TermsOfUse"));
const Waitlist = lazy(() => import("pages/Waitlist/Waitlist"));
const MeTime = lazy(() => import("pages/Insights/pages/MeetingTime/MeTime"));

const ActionsPage = lazy(() => import("pages/Actions/ActionsPage"));
const WorkspacePage = lazy(() => import("pages/Workspace/WorkspacePage"));
const CalendarPage = lazy(() => import("pages/Calendar/CalendarPage"));
const LoginPage = lazy(() => import("pages/Login/LoginPage"));
const PricingPage = lazy(() => import("pages/Pricing/PricingPage"));
const InvitationPage = lazy(() => import("pages/Invites/InvitationPage"));
const ExtensionEventPage = lazy(() =>
  import("extension/views/Meeting/Meeting")
);
const ExtensionAgendaPage = lazy(() => import("extension/views/Agenda/Agenda"));
const OnboardingCalendarPage = lazy(() => import("pages/OnboardingCalendar/OnboardingCalendarPage"));

const AllRoutes = () => {
  const { user } = useUser();
  // ! FOR DEVELOPMENT
  // const user = {
  //   email: "rahulvshah1974@gmail.com",
  // };
  useExtensioniFrameListener();

  return (
    <>
      <ErrorBoundary>
        <Suspense fallback={<Fallback />}>
          <Routes>
          {/* public routes */}
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/onboarding-calendar" element={<OnboardingCalendarPage />} />
          <Route path="/subscription" element={<PricingPage />} />
          <Route path="/invite" element={<InvitationPage />} />

          {/* private routes */}
          <Route element={<RequireAuth />}>
            {/* <Route path="/addusertoorg" element={<AddUserToOrg />} /> */}
            {/* <Route path="/addorganization" element={<CreateOrganizationForm />} /> */}
            <Route
              path="/extension/event/:eventId"
              element={<ExtensionEventPage />}
            />
            <Route path="/extension/agenda" element={<ExtensionAgendaPage />} />
            <Route path="/workspace" element={<WorkspacePage />} />
            <Route path="/actions" element={<ActionsPage />}>
              <Route path=":type" />
            </Route>
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/events" element={<Events />} />

            <Route path="/insights" element={<InsightsLayout />}>
              <Route path="analytics" element={<Analytics />} />
              <Route
                path=""
                element={<Navigate replace to="organization" />}
              />
              <Route path="me-time">
                <Route
                  index
                  element={<MeTime userEmail={user?.email} uid={user?.uid} />}
                />
                <Route
                  path="organizer"
                  element={<AsOrganizer email={user?.email} uid={user?.uid} />}
                />
                <Route
                  path="attendee"
                  element={<AsAttendee email={user?.email} uid={user?.uid} />}
                />
                <Route
                  path="optimize-next-week"
                  element={<OptimizeWeek />}
                />
              </Route>
              <Route path="direct-reportees">
                <Route index element={<DirectReportees />} />
                <Route path=":id" element={<DirectReport />} />
              </Route>
              <Route path="actions-insights" element={<ActionsInsights />} />
              <Route path="organization" element={<MyOrg />} />
            </Route>

            <Route element={<RequireRole allowedRoles={["admin"]} />}>
              <Route path="/organization" element={<AdminLayout />}>
                <Route
                  path=""
                  element={<Navigate replace to="general-info" />}
                />
                <Route path="general-info" element={<GeneralInfo />} />
                <Route path="manage-goals" element={<ManageGoals />} />
                <Route path="manage-users" element={<ManageUsers />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Route>
          </Route>

          {/* Default route */}
          <Route path="*" element={<Navigate replace to="/workspace" />} />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

export default AllRoutes;
